import { trackEvent } from '@patrianna/analytics'
import { useSocialButtonSpinner } from '@patrianna/shared-hooks'
import type { SocialAuthTypes } from '@patrianna/shared-patrianna-types/store/AuthModule'
import { getAuthFlowLoadingSelector } from '@patrianna/shared-store/auth'
import { isFacebookBrowser, isIOS } from '@patrianna/shared-utils/helpers'
import cx from 'classnames'

import Button from 'components/Button'
import { googleAuth } from 'services/google/actions'
import Loader from 'src/components/Loader'
import { GOOGLE_APP_ID } from 'src/config/apps'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import { openSnackbar } from 'store/modules/snackbar/actions'

import classes from './styles.module.scss'

type Props = {
  title: string
  authType: SocialAuthTypes
  disabled?: boolean
  skipTrackEvent?: boolean
}

function GoogleAuthButton(props: Props) {
  const dispatch = useAppDispatch()
  const isLoading = useAppSelector(getAuthFlowLoadingSelector)
  const { isSendRequest, setIsSendRequest } = useSocialButtonSpinner(isLoading)

  const onClick = () => {
    const fbBrowser = isFacebookBrowser()
    const iOS = isIOS()

    trackEvent('sign_up_started', {
      interaction_name: 'sign_up_started',
      sign_up_total_steps: 1,
      sign_up_current_step_number: 1,
      sign_up_interacted_element: 'google_button',
      sign_up_method: 'google',
    })

    if (!props.skipTrackEvent) {
      if (props.authType === 'REGISTRATION') {
        trackEvent('register_page_google_button')
      }

      if (props.authType === 'LOGIN') {
        trackEvent('login_page_google_button')
      }
    }

    if (fbBrowser && iOS) {
      dispatch(
        openSnackbar({
          message: `Please try to use a different sign-in method or visit ${process.env.ROOT_URL} from your web browser (Safari, Chrome etc)`,
        }),
      )

      trackEvent(`${props.authType?.toLowerCase()}_google_auth_fb_iOS_error`, {
        category: `${props.authType}_google_auth_fb_iOS_error`,
        label: `${props.authType}_google_auth_fb_iOS_error`,
      })
    } else {
      setIsSendRequest(true)
      dispatch(googleAuth(props.authType))
    }
  }

  if (GOOGLE_APP_ID) {
    return (
      <Button
        disabled={props.disabled || isLoading}
        className={cx(classes.root, isSendRequest && classes.loading)}
        onClick={onClick}
        data-test={props.title}
      >
        {isSendRequest && isLoading ? (
          <Loader size={28} />
        ) : (
          <>
            <span className={classes.buttonBox}>
              <img className={classes.icon} src={`${process.env.IMG_ORIGIN}/logos/google.svg`} alt='google logo' />
              <span className={classes.text}>{props.title}</span>
            </span>
          </>
        )}
      </Button>
    )
  }

  return null
}

export default GoogleAuthButton
